import { useState } from 'react';
import { Plane, Plus, Trash2, ArrowLeft, ArrowRight, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { FlightSegment } from '@/types/pricing';
import {
  isValidAirportCode,
  isValidAirlineCode,
  isValidBookingClass,
  isValidTime,
  isValidDate,
} from '@/utils/fqpGenerator';

interface FlightSegmentsStepProps {
  segments: FlightSegment[];
  onSegmentsChange: (segments: FlightSegment[]) => void;
  onBack: () => void;
  onNext: () => void;
}

function generateSegmentId(): string {
  return Math.random().toString(36).substring(2, 9);
}

function createEmptySegment(): FlightSegment {
  return {
    id: generateSegmentId(),
    origin: '',
    destination: '',
    airline: '',
    bookingClass: '',
    flightDate: '',
    departureTime: '',
    arrivalTime: '',
  };
}

interface SegmentErrors {
  origin?: string;
  destination?: string;
  airline?: string;
  bookingClass?: string;
  flightDate?: string;
  departureTime?: string;
  arrivalTime?: string;
}

function validateSegment(segment: FlightSegment): SegmentErrors {
  const errors: SegmentErrors = {};

  if (!segment.origin) {
    errors.origin = 'Required';
  } else if (!isValidAirportCode(segment.origin)) {
    errors.origin = 'Must be 3 letters';
  }

  if (!segment.destination) {
    errors.destination = 'Required';
  } else if (!isValidAirportCode(segment.destination)) {
    errors.destination = 'Must be 3 letters';
  }

  if (!segment.airline) {
    errors.airline = 'Required';
  } else if (!isValidAirlineCode(segment.airline)) {
    errors.airline = 'Must be 2 characters';
  }

  if (!segment.bookingClass) {
    errors.bookingClass = 'Required';
  } else if (!isValidBookingClass(segment.bookingClass)) {
    errors.bookingClass = 'Must be 1 letter';
  }

  if (!segment.flightDate) {
    errors.flightDate = 'Required';
  } else if (!isValidDate(segment.flightDate)) {
    errors.flightDate = 'Use DD.MM.YYYY';
  }

  if (!segment.departureTime) {
    errors.departureTime = 'Required';
  } else if (!isValidTime(segment.departureTime)) {
    errors.departureTime = 'Use HH:MM';
  }

  if (!segment.arrivalTime) {
    errors.arrivalTime = 'Required';
  } else if (!isValidTime(segment.arrivalTime)) {
    errors.arrivalTime = 'Use HH:MM';
  }

  return errors;
}

export function FlightSegmentsStep({
  segments,
  onSegmentsChange,
  onBack,
  onNext,
}: FlightSegmentsStepProps) {
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const updateSegment = (index: number, field: keyof FlightSegment, value: string) => {
    const updated = segments.map((seg, i) =>
      i === index ? { ...seg, [field]: value.toUpperCase() } : seg
    );
    onSegmentsChange(updated);
    setTouchedFields((prev) => new Set(prev).add(`${index}-${field}`));
  };

  const addSegment = () => {
    onSegmentsChange([...segments, createEmptySegment()]);
  };

  const removeSegment = (index: number) => {
    if (segments.length > 1) {
      onSegmentsChange(segments.filter((_, i) => i !== index));
    }
  };

  const allErrors = segments.map(validateSegment);
  const hasErrors = allErrors.some((errors) => Object.keys(errors).length > 0);
  const isValid = segments.length > 0 && !hasErrors;

  const getFieldError = (segmentIndex: number, field: keyof FlightSegment): string | undefined => {
    const fieldKey = `${segmentIndex}-${field}`;
    if (!touchedFields.has(fieldKey)) return undefined;
    return allErrors[segmentIndex]?.[field as keyof SegmentErrors];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Flight Segments</h2>
        <p className="mt-1 text-sm text-slate-600">
          Enter all flight segments for the fare quote. Add multiple segments for connecting flights.
        </p>
      </div>

      {/* Segments */}
      <div className="space-y-4">
        {segments.map((segment, index) => {
          const errors = allErrors[index];
          const hasSegmentErrors = Object.keys(errors).length > 0;

          return (
            <Card key={segment.id} className={hasSegmentErrors ? 'border-amber-200' : ''}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Plane className="h-4 w-4 text-blue-600" />
                    Segment {index + 1}
                    {segment.origin && segment.destination && (
                      <span className="text-slate-500 font-normal">
                        ({segment.origin} → {segment.destination})
                      </span>
                    )}
                  </CardTitle>
                  {segments.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeSegment(index)}
                      className="h-8 w-8 text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Route Row */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor={`origin-${index}`} className="text-xs">
                      Origin
                    </Label>
                    <Input
                      id={`origin-${index}`}
                      placeholder="FRA"
                      value={segment.origin}
                      onChange={(e) => updateSegment(index, 'origin', e.target.value)}
                      onBlur={() =>
                        setTouchedFields((prev) => new Set(prev).add(`${index}-origin`))
                      }
                      maxLength={3}
                      className={getFieldError(index, 'origin') ? 'border-red-300' : ''}
                    />
                    {getFieldError(index, 'origin') && (
                      <p className="text-xs text-red-500">{getFieldError(index, 'origin')}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`destination-${index}`} className="text-xs">
                      Destination
                    </Label>
                    <Input
                      id={`destination-${index}`}
                      placeholder="MUC"
                      value={segment.destination}
                      onChange={(e) => updateSegment(index, 'destination', e.target.value)}
                      onBlur={() =>
                        setTouchedFields((prev) => new Set(prev).add(`${index}-destination`))
                      }
                      maxLength={3}
                      className={getFieldError(index, 'destination') ? 'border-red-300' : ''}
                    />
                    {getFieldError(index, 'destination') && (
                      <p className="text-xs text-red-500">{getFieldError(index, 'destination')}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`airline-${index}`} className="text-xs">
                      Airline
                    </Label>
                    <Input
                      id={`airline-${index}`}
                      placeholder="LH"
                      value={segment.airline}
                      onChange={(e) => updateSegment(index, 'airline', e.target.value)}
                      onBlur={() =>
                        setTouchedFields((prev) => new Set(prev).add(`${index}-airline`))
                      }
                      maxLength={2}
                      className={getFieldError(index, 'airline') ? 'border-red-300' : ''}
                    />
                    {getFieldError(index, 'airline') && (
                      <p className="text-xs text-red-500">{getFieldError(index, 'airline')}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`bookingClass-${index}`} className="text-xs">
                      Class
                    </Label>
                    <Input
                      id={`bookingClass-${index}`}
                      placeholder="Y"
                      value={segment.bookingClass}
                      onChange={(e) => updateSegment(index, 'bookingClass', e.target.value)}
                      onBlur={() =>
                        setTouchedFields((prev) => new Set(prev).add(`${index}-bookingClass`))
                      }
                      maxLength={1}
                      className={getFieldError(index, 'bookingClass') ? 'border-red-300' : ''}
                    />
                    {getFieldError(index, 'bookingClass') && (
                      <p className="text-xs text-red-500">{getFieldError(index, 'bookingClass')}</p>
                    )}
                  </div>
                </div>

                {/* Date/Time Row */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor={`flightDate-${index}`} className="text-xs">
                      Flight Date
                    </Label>
                    <Input
                      id={`flightDate-${index}`}
                      placeholder="15.06.2024"
                      value={segment.flightDate}
                      onChange={(e) => updateSegment(index, 'flightDate', e.target.value)}
                      onBlur={() =>
                        setTouchedFields((prev) => new Set(prev).add(`${index}-flightDate`))
                      }
                      className={getFieldError(index, 'flightDate') ? 'border-red-300' : ''}
                    />
                    {getFieldError(index, 'flightDate') && (
                      <p className="text-xs text-red-500">{getFieldError(index, 'flightDate')}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`departureTime-${index}`} className="text-xs">
                      Departure
                    </Label>
                    <Input
                      id={`departureTime-${index}`}
                      placeholder="18:50"
                      value={segment.departureTime}
                      onChange={(e) => updateSegment(index, 'departureTime', e.target.value)}
                      onBlur={() =>
                        setTouchedFields((prev) => new Set(prev).add(`${index}-departureTime`))
                      }
                      className={getFieldError(index, 'departureTime') ? 'border-red-300' : ''}
                    />
                    {getFieldError(index, 'departureTime') && (
                      <p className="text-xs text-red-500">
                        {getFieldError(index, 'departureTime')}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`arrivalTime-${index}`} className="text-xs">
                      Arrival
                    </Label>
                    <Input
                      id={`arrivalTime-${index}`}
                      placeholder="19:40"
                      value={segment.arrivalTime}
                      onChange={(e) => updateSegment(index, 'arrivalTime', e.target.value)}
                      onBlur={() =>
                        setTouchedFields((prev) => new Set(prev).add(`${index}-arrivalTime`))
                      }
                      className={getFieldError(index, 'arrivalTime') ? 'border-red-300' : ''}
                    />
                    {getFieldError(index, 'arrivalTime') && (
                      <p className="text-xs text-red-500">{getFieldError(index, 'arrivalTime')}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Add Segment Button */}
      <Button variant="outline" onClick={addSegment} className="w-full">
        <Plus className="mr-2 h-4 w-4" />
        Add Another Segment
      </Button>

      {/* Validation Warning */}
      {hasErrors && touchedFields.size > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please fill in all required fields with valid values before continuing.
          </AlertDescription>
        </Alert>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Button onClick={onNext} disabled={!isValid}>
          Generate Fare Quote
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
